import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { useQuery } from '@tanstack/react-query';
import {
  accountCreatedSchema,
  authStatusSchema,
  type AccountCreated,
  type AuthStatus,
  type SetupRequest,
} from '@dishly/shared';
import { api, isNetworkError } from './api.ts';
import { getLocale } from '../paraglide/runtime.js';
import { clearMirror } from './mirror.ts';

const LAST_AUTH_KEY = 'dishly.lastAuth';

/** Tells the server the UI language (pushes are written in it). */
export async function saveServerLocale(locale: 'de' | 'en'): Promise<void> {
  await api('/api/auth/me', { method: 'PATCH', body: JSON.stringify({ locale }) });
}

export type AuthState = AuthStatus & { offline: boolean };

function rememberAuth(status: AuthStatus | null): void {
  try {
    if (status?.user) localStorage.setItem(LAST_AUTH_KEY, JSON.stringify(status));
    else localStorage.removeItem(LAST_AUTH_KEY);
  } catch {
    // Storage unavailable (private mode): an offline start just shows the login screen.
  }
}

function lastKnownAuth(): AuthStatus {
  try {
    const parsed = authStatusSchema.safeParse(
      JSON.parse(localStorage.getItem(LAST_AUTH_KEY) ?? ''),
    );
    if (parsed.success) return parsed.data;
  } catch {
    // Nothing stored.
  }
  return { user: null, mustAddPasskey: false };
}

/**
 * Offline-first: when the server is unreachable we trust the last known sign-in so the installed
 * app still opens with cached data. The server re-checks on every API call anyway.
 */
async function fetchAuthState(): Promise<AuthState> {
  try {
    const status = authStatusSchema.parse(await api('/api/auth/status'));
    rememberAuth(status);
    // The language was switched on this device before the server kept track of it.
    const locale = getLocale();
    if (status.user && status.user.locale !== locale && !status.mustAddPasskey) {
      void saveServerLocale(locale).catch(() => undefined);
    }
    return { ...status, offline: false };
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    return { ...lastKnownAuth(), offline: true };
  }
}

export const authQueryKey = ['auth', 'status'] as const;

export function useAuth() {
  return useQuery({
    queryKey: authQueryKey,
    queryFn: fetchAuthState,
    networkMode: 'always',
    staleTime: 5 * 60_000,
  });
}

/** Runs a registration ceremony: options from `optionsPath`, the credential to `verifyPath`. */
export async function registerWith<T>(
  optionsPath: string,
  verifyPath: string,
  body: object = {},
): Promise<T> {
  const optionsJSON = await api<PublicKeyCredentialCreationOptionsJSON>(optionsPath, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const credential = await startRegistration({ optionsJSON });
  return api<T>(verifyPath, {
    method: 'POST',
    body: JSON.stringify({ ...body, credential }),
  });
}

export async function setUpHousehold(request: SetupRequest): Promise<AccountCreated> {
  return accountCreatedSchema.parse(
    await registerWith('/api/setup/options', '/api/setup/verify', request),
  );
}

/**
 * Passkey sign-in. With `autofill`, waits for the user to pick a passkey from the browser's
 * autofill (conditional UI) instead of opening the passkey sheet.
 */
export async function signIn(autofill = false): Promise<void> {
  const optionsJSON = await api<PublicKeyCredentialRequestOptionsJSON>('/api/auth/login/options', {
    method: 'POST',
  });
  const credential = await startAuthentication({ optionsJSON, useBrowserAutofill: autofill });
  await api('/api/auth/login/verify', { method: 'POST', body: JSON.stringify(credential) });
}

/** Recovery code → a session that must add a passkey first. */
export async function recover(code: string): Promise<void> {
  await api('/api/auth/recover', { method: 'POST', body: JSON.stringify({ code }) });
}

export async function redeemLoginLink(token: string): Promise<void> {
  await api('/api/auth/login-link', { method: 'POST', body: JSON.stringify({ token }) });
}

export async function addPasskey(label: string | null): Promise<void> {
  await registerWith('/api/passkeys/register/options', '/api/passkeys/register/verify', {
    label,
  });
}

export async function signOut(): Promise<void> {
  await api('/api/auth/logout', { method: 'POST' });
  rememberAuth(null);
  // The next person on this device must not find this household's recipes.
  await clearMirror().catch(() => undefined);
}

/** Message key for a failed passkey ceremony. */
export function passkeyErrorKey(err: unknown): 'auth_cancelled' | 'auth_failed' {
  return err instanceof Error && err.name === 'NotAllowedError' ? 'auth_cancelled' : 'auth_failed';
}
